import { SubrouteComponent } from '../../types';
import { KizenTypography } from '__app/typography';
import { StyledTableToolbar } from '../../styles';
import {
  PaginationWrapper,
  StyledMetaData,
  StyledTableScrollContainer,
  TableWrapper,
} from './styles';
import SmartConnectorService from 'services/SmartConnectorService';
import TablePagination from '__components/Tables/Big/TablePagination';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import { TRow } from '__components/Kizen/Table';
import BasicTable from '__components/Tables/Basic';
import { useAsync } from 'react-use';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { useColumns } from './columns';
import { DEFAULT_PAGINATION_SETTINGS } from './constants';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { useCallback, useState } from 'react';
import useModal from '__components/Modals/useModal';
import { SmartConnectorHistory } from '__pages/SmartConnectors/types';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useTranslation } from 'react-i18next';

export const TimelineTable: SubrouteComponent = ({ smartConnector }) => {
  const { t } = useTranslation();

  const {
    page,
    pageSize,
    ordering,
    handleChangePage,
    handleChangePageSize,
    headData,
  } = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);

  const { value: { results = EMPTY_ARRAY, count = 0 } = {}, loading } =
    useAsync(async () => {
      return await SmartConnectorService.getSmartConnectorHistory(
        smartConnector.id,
        {
          page,
          page_size: pageSize,
          ordering,
        }
      );
    }, [page, pageSize, ordering, smartConnector.id]);

  const [detailsModalProps, , { show: showDetailsModalProp }] = useModal();

  const [smartConnectorHistory, setSmartConnectorHistory] =
    useState<SmartConnectorHistory | null>(null);

  const handleSelectAction = useCallback(
    ({ value }: { value: 'view' }, data: SmartConnectorHistory) => {
      if (value === 'view') {
        showDetailsModalProp();
        setSmartConnectorHistory(data);
      }
    },
    [showDetailsModalProp]
  );

  const columns = useColumns(handleSelectAction);

  return (
    <>
      <StyledTableToolbar>
        <div />
        <KizenTypography as="h2" type="header">
          {t('SmartConnector History Log')}
        </KizenTypography>
        <div />
      </StyledTableToolbar>
      <TableWrapper>
        <StyledTableScrollContainer bottom top gradient={<Gradient />}>
          <BasicTable
            key={`table-${smartConnector.id}`}
            stickyHeader
            head={<TRow head columns={columns} data={headData} />}
            data-qa="smartConnector-history-table"
            staleData={loading}
          >
            {count ? null : <TRow />}
            {results.map((data: any) => (
              <TRow key={data.id} columns={columns} data={data} />
            ))}
          </BasicTable>
        </StyledTableScrollContainer>
      </TableWrapper>
      <PaginationWrapper>
        <TablePagination
          page={page}
          perPage={pageSize}
          totalCount={count}
          onChangePage={handleChangePage}
          onChangePerPage={handleChangePageSize}
        />
      </PaginationWrapper>
      <BasicModal
        {...detailsModalProps}
        heading={t('Timeline Details')}
        leftBtn={null}
        buttonText={t('Close')}
        actionBtnColor="blue"
        size="medium"
      >
        <StyledMetaData>
          {JSON.stringify(smartConnectorHistory?.data || {}, null, 2)}
        </StyledMetaData>
      </BasicModal>
    </>
  );
};
